import React from 'react';
import SubscriptionComparisonCard from '@/components/subscriptions/comparison/SubscriptionComparisonCard';
import SubscriptionAdvantageList from '@/components/subscriptions/comparison/SubscriptionAdvantageList';
import useTierNavigation from '@/hooks/useTierNavigation';
import PricingCarouselButtons from './PricingCarouselButtons';
import { BillingPeriod } from '@/types/billingPeriod';
import { SubscriptionTier } from '@/actions/dtos/subscription';
import { useAccount } from '@/hooks/useAccount';

interface SubscriptionsTableProps {
  billingPeriod: BillingPeriod;
  discountCode?: string;
  renderCardButton?: (tier: SubscriptionTier, isCurrent: boolean) => React.ReactNode;
}

const SubscriptionsTable = ({ billingPeriod, discountCode, renderCardButton }: SubscriptionsTableProps) => {
  const { currentTier, handlePrevTier, handleNextTier } = useTierNavigation();
  const { customer } = useAccount();
  const customerTier = customer?.existingStripeSubscription
    ? customer.currentSubscription.tier
    : SubscriptionTier.BASIC;
  const tiers = Object.values(SubscriptionTier);
  const currentTierValue = tiers[currentTier];

  return (
    <div className="relative flex w-full flex-col items-center py-4 text-base">
      <div className="relative grid w-full max-w-full grid-cols-2 gap-4 lg:grid-cols-5">
        <div className="mt-14">
          <SubscriptionAdvantageList discountCode={discountCode} />
        </div>

        {/* Responsive Navigation for Medium Screens */}
        <div className="hidden md:ml-8 md:block lg:hidden">
          <PricingCarouselButtons
            onPrev={handlePrevTier}
            onNext={handleNextTier}
            disablePrev={currentTier === 0}
            disableNext={currentTier >= tiers.length - 1}
          >
            <SubscriptionComparisonCard
              tier={tiers[currentTier]}
              billingPeriod={billingPeriod}
              discountCode={discountCode}
              renderCardButton={
                renderCardButton
                  ? () => renderCardButton(currentTierValue, customerTier === currentTierValue)
                  : undefined
              }
            />
          </PricingCarouselButtons>
        </div>

        {/* Responsive Navigation for Small Screens */}
        <div className="md:hidden">
          <SubscriptionComparisonCard
            tier={tiers[currentTier]}
            billingPeriod={billingPeriod}
            discountCode={discountCode}
            renderCardButton={
              renderCardButton ? () => renderCardButton(currentTierValue, customerTier === currentTierValue) : undefined
            }
          />
          <PricingCarouselButtons
            onPrev={handlePrevTier}
            onNext={handleNextTier}
            disablePrev={currentTier === 0}
            disableNext={currentTier >= tiers.length - 1}
          />
        </div>

        {/* Comparison Cards for Large Screens */}
        <div className="flex gap-4 max-lg:hidden">
          {tiers.map((tier, idx) => {
            const isCurrent = customerTier === tier;
            return (
              <SubscriptionComparisonCard
                key={idx}
                tier={tier}
                billingPeriod={billingPeriod}
                discountCode={discountCode}
                renderCardButton={renderCardButton ? () => renderCardButton(tier, isCurrent) : undefined}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionsTable;
